import { FocusEvent, useState } from 'react';
import { isPast, isValid, isBefore, parseISO } from 'date-fns';
import InputMask from 'react-input-mask';

import { OnChangeOrOnBlurType } from '../types/global';
import {
  isValidDate,
  parseAsUtc,
  formatAsUtc,
} from '@aldebaran/common/util/date';
import { parseInlineMarkdown } from '@/util/parseInlineMarkdown';
import { Input } from './ui/input';

export interface ResponseDateFieldProps {
  value?: number;
  label?: string;
  multiline?: boolean;
  includeTime?: boolean;
  disabled?: boolean;
  withIcon?: boolean;
  error?: boolean;
  alwaysShowLabel?: boolean;
}

export const ResponseDOBField = ({
  value,
  label,
  onChange,
  onBlur,
  alwaysShowLabel,
  error,
}: ResponseDateFieldProps & OnChangeOrOnBlurType<number | null>) => {
  const [inputValue, setInputValue] = useState(
    isValidDate(value) ? formatAsUtc(value, 'dd/MM/yyyy') : '',
  );
  const [isInvalid, setIsInvalid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const validateDate = (dateStr: string): boolean => {
    if (!dateStr || dateStr.trim() === '') return true;

    if (dateStr.length < 10) return false;

    const date = parseAsUtc(dateStr, 'dd/MM/yyyy');
    if (!isValid(date)) return false;

    const year1900 = parseISO('1900-01-01');

    return isPast(date) && !isBefore(date, year1900);
  };

  const onSubmitResponse = (
    e:
      | FocusEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChangeFunction?: (value: number | null) => void,
  ) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    const valid = validateDate(newValue);
    setIsInvalid(!valid);

    if (newValue && onChangeFunction) {
      const nextValue = parseAsUtc(newValue, 'dd/MM/yyyy');
      if (valid) {
        onChangeFunction(nextValue.getTime());
      } else {
        onChangeFunction(null);
      }
    } else if (onChangeFunction) {
      onChangeFunction(null);
    }
  };

  const showError =
    (!isFocused && isInvalid && inputValue.trim() !== '') || error;

  return (
    <div className="flex flex-col gap-1 w-full">
      {alwaysShowLabel && label && (
        <p className="text-sm font-medium text-indigo-900">
          {parseInlineMarkdown(label)}
        </p>
      )}
      <InputMask
        mask="99/99/9999"
        value={inputValue}
        alwaysShowMask={false}
        onChange={(e) => onSubmitResponse(e, onChange)}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          const raw = e.target.value;
          const valid = validateDate(raw);
          const blurValue =
            raw && valid ? parseAsUtc(raw, 'dd/MM/yyyy').getTime() : null;
          onSubmitResponse(e, onChange);
          onBlur?.(blurValue);
        }}
      >
        {/* @ts-expect-error InputMask children render props */}
        {(inputProps: React.ComponentPropsWithoutRef<typeof Input>) => (
          <Input
            placeholder={label ?? 'jj/mm/aaaa'}
            aria-invalid={showError}
            {...inputProps}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        )}
      </InputMask>
    </div>
  );
};
